import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy, Flame, ListChecks, Lock, Sparkles, KeyRound, Loader2, Briefcase, MapPin, Clock, Link2, ChevronDown, ChevronUp, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { SeedAvatar } from "@/components/SeedAvatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  getMyBadges,
  getMyStats,
  getMyRanks,
  markBadgesSeen,
  authChangePin,
  getMyProfile,
} from "@/lib/afritask";

const TASK_TYPE_LABELS = {
  text_classification: "Text classification",
  image_labeling: "Image labeling",
  translation: "Translation",
  audio_transcription: "Audio transcription",
  voice_rating: "Voice / audio rating",
};

// Skills that are actually language proficiencies get bucketed separately for a
// cleaner two-column layout on the career profile card.
const LANGUAGE_KEYWORDS = ["native", "fluent", "basic"];
const LANGUAGE_EXACT = new Set(["Pidgin", "Efik", "Ibibio", "Fulfulde", "Kanuri"]);
const isLanguageSkill = (s) =>
  LANGUAGE_EXACT.has(s) || LANGUAGE_KEYWORDS.some((k) => s.toLowerCase().includes(k));

const fmtNaira = (n) => `₦${Number(n).toLocaleString("en-NG")}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");

const TIER_STYLES = {
  bronze: "border-amber-700/40 bg-amber-50/60",
  silver: "border-neutral-400/50 bg-neutral-50",
  gold: "border-amber-400 bg-amber-50",
  platinum: "border-amber-400 bg-amber-50",
};

const TIER_BADGE = {
  bronze: "bg-amber-100 text-amber-800",
  silver: "bg-neutral-200 text-neutral-800",
  gold: "bg-amber-200 text-amber-900",
  platinum: "bg-amber-200 text-amber-900",
};

const CATEGORIES = [
  { key: "earnings", label: "Top Earners" },
  { key: "success_rate", label: "Success Rate" },
  { key: "tasks_completed", label: "Most Completed" },
  { key: "streak", label: "Longest Streaks" },
];

export default function Profile() {
  const { me, wallet } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState(null);
  const [ranks, setRanks] = useState(null);
  const [careerProfile, setCareerProfile] = useState(null);
  const [rankPeriod, setRankPeriod] = useState("week");

  const load = async () => {
    try {
      const [s, b, r, p] = await Promise.all([
        getMyStats().catch(() => null),
        getMyBadges().catch(() => null),
        getMyRanks().catch(() => null),
        getMyProfile().catch(() => null),
      ]);
      setStats(s);
      setBadges(b);
      setRanks(r);
      setCareerProfile(p);
      if (b?.unseen_count > 0) {
        await markBadgesSeen().catch(() => {});
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!me) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.user_id]);

  const initials = me?.display_name
    ? me.display_name.replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase()
    : me?.username
    ? me.username.replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase()
    : "AT";

  const successRatePct = Math.round((stats?.success_rate || 0) * 100);
  void successRatePct; // (kept — surfaced on Dashboard, no longer duplicated here)

  const memberSince = me?.created_at
    ? new Date(me.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })
    : null;

  // Compact rank subset: only categories the user is actually ranked in.
  const currentRanks = ranks?.ranks?.[rankPeriod] || {};
  const rankedCategories = CATEGORIES.filter((c) => {
    const r = currentRanks[c.key];
    return typeof r === "number" && r > 0;
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Compact identity header */}
      <Card>
        <CardContent className="flex items-center gap-3 p-4">
          <SeedAvatar seed={me?.username || initials} size={64} />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-semibold tracking-tight" data-testid="profile-username">
              {me?.display_name || me?.username || "…"}
            </h2>
            <p className="mt-0.5 truncate font-mono text-[11px] text-neutral-500" data-testid="profile-handle">
              {me?.username || (me?.user_id ? `#${me.user_id.slice(0, 6).toUpperCase()}` : "")}
            </p>
            <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs tabular-nums text-neutral-500">
              <span className="font-medium text-neutral-700">
                {wallet ? `${wallet.work_points.toLocaleString()} pts` : "— pts"}
              </span>
              <span className="text-neutral-300">·</span>
              <span className="font-medium text-emerald-700">
                {wallet ? `₦${wallet.earnings_ngn.toLocaleString()} earned` : "— earned"}
              </span>
              {memberSince && (
                <>
                  <span className="text-neutral-300">·</span>
                  <span>Member since {memberSince}</span>
                </>
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Career Profile (Phase 8.5) */}
      <CareerProfileSection profile={careerProfile} />

      {/* Stats — 3 across, compact, matches Dashboard amber accent */}
      <div>
        <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Stats</h3>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <StatCard
            icon={ListChecks}
            tone="amber"
            label="Completed"
            value={String(stats?.total_tasks_approved ?? 0)}
            sub={`${stats?.total_tasks_entered ?? 0} entered`}
            testid="stat-tasks-completed"
          />
          <StatCard
            icon={Flame}
            tone="rose"
            label="Streak"
            value={`${stats?.current_streak_days ?? 0}d`}
            sub={`Best: ${stats?.longest_streak_days ?? 0}d`}
            testid="stat-streak"
          />
          <StatCard
            icon={Trophy}
            tone="emerald"
            label="Lifetime earned"
            value={fmtNaira(stats?.lifetime_earnings_ngn ?? 0)}
            sub={`${stats?.tasks_this_week ?? 0} this week`}
            testid="stat-lifetime"
          />
        </div>
      </div>

      {/* Rank card */}
      <Card>
        <CardContent className="space-y-4 p-4 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-base font-semibold">Your rank</h3>
              <p className="text-xs text-neutral-500">Across 4 leaderboards</p>
            </div>
            <Tabs value={rankPeriod} onValueChange={setRankPeriod}>
              <TabsList>
                <TabsTrigger value="week" data-testid="rank-period-week">Week</TabsTrigger>
                <TabsTrigger value="month" data-testid="rank-period-month">Month</TabsTrigger>
                <TabsTrigger value="all_time" data-testid="rank-period-all_time">All-time</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          {rankedCategories.length === 0 ? (
            <p
              className="rounded-lg bg-neutral-50 px-3 py-2 text-xs text-neutral-500"
              data-testid={`rank-empty-${rankPeriod}`}
            >
              No ranking yet for this period. Complete a task to appear on the leaderboard.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {rankedCategories.map((c) => (
                <div
                  key={c.key}
                  className="rounded-lg border border-neutral-100 bg-white p-3"
                  data-testid={`rank-cell-${rankPeriod}-${c.key}`}
                >
                  <p className="text-xs text-neutral-500">{c.label}</p>
                  <p className="mt-1 text-2xl font-bold tabular-nums text-amber-800">
                    #{currentRanks[c.key]}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Badges — earned (rendered only when there's at least one) */}
      {badges && badges.earned.length > 0 && (
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold">
              Earned badges
              <span className="ml-2 text-sm font-normal text-neutral-500">
                {badges.counts.earned} / {badges.counts.total}
              </span>
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {badges.earned.map((b) => (
              <Card
                key={b.code}
                className={`border-2 ${TIER_STYLES[b.tier]}`}
                data-testid={`badge-earned-${b.code}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <span className="text-4xl">{b.icon}</span>
                    <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${TIER_BADGE[b.tier]}`}>
                      {b.tier}
                    </span>
                  </div>
                  <p className="mt-3 text-sm font-semibold">{b.name}</p>
                  <p className="mt-0.5 line-clamp-2 text-xs text-neutral-500">{b.description}</p>
                  <p className="mt-2 text-[10px] text-neutral-400">
                    Earned {fmtDate(b.earned_at)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Badges — progress (compact rows, top 4 + Show all toggle) */}
      {badges && badges.in_progress.length > 0 && (
        <InProgressBadges items={badges.in_progress} />
      )}

      {/* Security — Change PIN */}
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Security</p>
            <p className="mt-1 text-xs text-neutral-500">Manage your PIN.</p>
          </div>
          <ChangePinButton />
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ icon: Icon, tone, label, value, sub, testid }) {
  const tones = {
    amber: "text-amber-700",
    emerald: "text-emerald-600",
    rose: "text-rose-600",
  };
  return (
    <Card
      className="rounded-xl border border-amber-300 bg-white shadow-sm ring-1 ring-amber-200/70 transition hover:border-amber-400 hover:ring-amber-300 hover:shadow-md"
      data-testid={testid}
    >
      <CardContent className="p-3">
        <Icon className={`h-4 w-4 ${tones[tone] || "text-amber-700"}`} />
        <p className="mt-1.5 truncate text-[11px] font-medium uppercase tracking-wide text-neutral-500">
          {label}
        </p>
        <p className="mt-0.5 truncate text-base font-semibold tabular-nums text-neutral-900">
          {value}
        </p>
        {sub && <p className="mt-0.5 truncate text-[10px] text-neutral-500">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function CareerProfileSection({ profile }) {
  const navigate = useNavigate();
  const hasProfile = profile && (profile.summary || (profile.skills && profile.skills.length > 0) || profile.primary_work_area);

  if (!hasProfile) {
    return (
      <Card
        className="border-2 border-dashed border-amber-200 bg-amber-50/40"
        data-testid="career-profile-empty"
      >
        <CardContent className="flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Briefcase className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-neutral-900">Complete your career profile</p>
              <p className="mt-0.5 text-xs text-neutral-600">
                Get better task matches based on your skills, languages, and experience.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => navigate("/app/profile-setup")}
            className="bg-amber-700 hover:bg-amber-800"
            data-testid="career-profile-setup-btn"
          >
            Set up profile
          </Button>
        </CardContent>
      </Card>
    );
  }

  const allSkills = profile.skills || [];
  const languages = allSkills.filter(isLanguageSkill);
  const otherSkills = allSkills.filter((s) => !isLanguageSkill(s));
  const prefTasks = profile.preferred_task_types || [];
  const completeness = profile.profile_completeness ?? 0;
  const location = [profile.city, profile.state].filter(Boolean).join(", ");

  return (
    <Card data-testid="career-profile-section">
      <CardContent className="space-y-4 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-amber-700" />
            <h3 className="text-base font-semibold">Career profile</h3>
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
              {completeness}% complete
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate("/app/profile-setup")}
            data-testid="career-profile-edit-btn"
          >
            <Pencil className="mr-1.5 h-3.5 w-3.5" />
            Edit profile
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2">
          {/* Left column — narrative + tags */}
          <div className="space-y-4">
            {profile.summary && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">Summary</p>
                <p className="mt-1 text-sm leading-relaxed text-neutral-700" data-testid="career-profile-summary">
                  {profile.summary}
                </p>
              </div>
            )}
            {otherSkills.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
                  Skills <span className="text-neutral-400">· {otherSkills.length}</span>
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="career-profile-skills">
                  {otherSkills.map((s) => (
                    <span
                      key={s}
                      className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-900 ring-1 ring-inset ring-amber-100"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {languages.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
                  Languages <span className="text-neutral-400">· {languages.length}</span>
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="career-profile-languages">
                  {languages.map((s) => (
                    <span
                      key={s}
                      className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-inset ring-emerald-100"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {prefTasks.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">Preferred task types</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="career-profile-preferred-tasks">
                  {prefTasks.map((code) => (
                    <span
                      key={code}
                      className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-medium text-neutral-700"
                    >
                      {TASK_TYPE_LABELS[code] || code}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column — structured facts (iOS grouped-list style) */}
          <div className="divide-y divide-neutral-100 rounded-lg border border-neutral-100 bg-neutral-50/40 px-4">
            <FactRow icon={Briefcase} label="Primary work area" value={profile.primary_work_area} testid="career-fact-work-area" />
            <FactRow icon={Clock} label="Years of experience" value={profile.years_experience_bucket} testid="career-fact-years" />
            <FactRow icon={MapPin} label="Location" value={location || null} testid="career-fact-location" />
            <FactRow icon={Sparkles} label="Availability" value={profile.availability} testid="career-fact-availability" />
            {profile.linkedin_url ? (
              <FactRow
                icon={Link2}
                label="Portfolio / LinkedIn"
                value={
                  <a
                    href={profile.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="truncate text-amber-700 hover:underline"
                    data-testid="career-fact-linkedin"
                  >
                    {profile.linkedin_url.replace(/^https?:\/\//, "")}
                  </a>
                }
              />
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FactRow({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-2.5 py-2.5" data-testid={testid}>
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-neutral-400" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{label}</p>
        <p className="mt-0.5 truncate text-sm font-medium text-neutral-800">
          {value || <span className="text-neutral-300">Not set</span>}
        </p>
      </div>
    </div>
  );
}

function InProgressBadges({ items }) {
  const [expanded, setExpanded] = useState(false);
  // Sort by progress % descending, then by criteria proximity
  const sorted = [...items].sort((a, b) => (b.progress?.pct || 0) - (a.progress?.pct || 0));
  const visible = expanded ? sorted : sorted.slice(0, 4);
  const remaining = sorted.length - 4;

  return (
    <div data-testid="in-progress-badges">
      <h3 className="mb-3 text-base font-semibold">
        Badges to unlock
        <span className="ml-2 text-sm font-normal text-neutral-500">{sorted.length} total</span>
      </h3>
      <Card>
        <CardContent className="divide-y divide-neutral-100 p-0">
          {visible.map((b) => (
            <div
              key={b.code}
              className="flex items-center gap-3 px-4 py-3"
              data-testid={`badge-locked-${b.code}`}
            >
              <span className="text-2xl opacity-40 grayscale">{b.icon}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="truncate text-sm font-semibold">{b.name}</p>
                  <Lock className="h-3 w-3 shrink-0 text-neutral-400" />
                  <span className={`ml-auto rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${TIER_BADGE[b.tier]}`}>
                    {b.tier}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <Progress value={b.progress?.pct || 0} className="h-1.5 flex-1" />
                  <span className="shrink-0 text-[11px] tabular-nums text-neutral-500">
                    {b.progress?.hint} · +{b.points_reward} pts
                  </span>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      {remaining > 0 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-neutral-300 py-2 text-xs font-medium text-neutral-600 transition hover:bg-neutral-50"
          data-testid="in-progress-badges-toggle"
        >
          {expanded ? (
            <>Show less <ChevronUp className="h-3.5 w-3.5" /></>
          ) : (
            <>Show all {sorted.length} badges <ChevronDown className="h-3.5 w-3.5" /></>
          )}
        </button>
      )}
    </div>
  );
}

function ChangePinButton() {
  const [open, setOpen] = useState(false);
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [newPinConfirm, setNewPinConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCurrentPin(""); setNewPin(""); setNewPinConfirm("");
  };

  const validNew = (newPin.length === 4 || newPin.length === 6) && newPin === newPinConfirm;
  const canSubmit = currentPin.length >= 4 && validNew && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await authChangePin({
        current_pin: currentPin,
        new_pin: newPin,
        new_pin_confirm: newPinConfirm,
      });
      toast.success("PIN changed successfully");
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change PIN");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="change-pin-btn"
      >
        <KeyRound className="mr-2 h-4 w-4" />
        Change PIN
      </Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="sm:max-w-md" data-testid="change-pin-dialog">
          <DialogHeader>
            <DialogTitle>Change your PIN</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Current PIN</Label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={currentPin}
                onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ""))}
                placeholder="Enter current PIN"
                className="text-center tracking-widest"
                data-testid="change-pin-current"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label>New PIN</Label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                placeholder="4 or 6 digits"
                className="text-center tracking-widest"
                data-testid="change-pin-new"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Confirm new PIN</Label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={newPinConfirm}
                onChange={(e) => setNewPinConfirm(e.target.value.replace(/\D/g, ""))}
                placeholder="Repeat new PIN"
                className="text-center tracking-widest"
                data-testid="change-pin-confirm"
              />
            </div>
            {newPin && !(newPin.length === 4 || newPin.length === 6) && (
              <p className="text-xs text-red-500">PIN must be exactly 4 or 6 digits.</p>
            )}
            {newPin && newPinConfirm && newPin !== newPinConfirm && (
              <p className="text-xs text-red-500">New PIN and confirmation don&apos;t match.</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setOpen(false); reset(); }}
              disabled={busy}
              data-testid="change-pin-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={!canSubmit}
              className="bg-amber-700 hover:bg-amber-800"
              data-testid="change-pin-submit"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Update PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
